import { ButtonHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

import { LoadingDots } from '../LoadingDots';

type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  // Publishing-portal redesign variants (Figma "button-primary" family).
  | 'primary'
  | 'primaryBlue'
  | 'outlineWhite'
  | 'outlineMuted'
  | 'outlineSuccess'
  | 'dangerOutline'
  | 'ghostMuted';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  submitting?: boolean;
  variant?: ButtonVariant;
}

const BASE_CLASSES =
  'border font-bold h-10 px-3.5 rounded-xl text-sm disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2 transition-[background-color,border-color,color,opacity,transform] duration-150 ease-out active:scale-[0.98] disabled:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-950';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  secondary:
    'bg-neutral-100 border-neutral-200 text-neutral-900 hover:bg-neutral-200 focus:bg-neutral-200 focus:border-neutral-300 active:bg-neutral-300',
  outline:
    'bg-transparent border-neutral-300 text-neutral-700 hover:bg-neutral-50 focus:bg-neutral-50 focus:border-neutral-400 active:bg-neutral-100',
  ghost:
    'bg-transparent border-transparent text-neutral-700 hover:bg-neutral-100 focus:bg-neutral-100 active:bg-neutral-200',
  destructive:
    'bg-red-600 border-transparent text-white hover:bg-red-700 focus:bg-red-700 focus:border-red-300 active:bg-red-800',
  default:
    'bg-linear-[180deg,#2EA597_-4.4%,#2B8D8F_41.09%,#2B888F_100%] border-transparent text-white hover:bg-linear-[180deg,#278C80_-4.4%,#237475_41.09%,#237075_100%] focus:bg-linear-[180deg,#278C80_-4.4%,#237475_41.09%,#237075_100%] focus:border-neutral-300 active:bg-linear-[180deg,#278C80_-4.4%,#1F6566_41.09%,#1F6166_100%] disabled:hover:bg-linear-[180deg,#2EA597_-4.4%,#2B8D8F_41.09%,#2B888F_100%]',
  primary:
    'bg-white border-transparent text-black px-4 hover:bg-seeker-200 focus-visible:bg-seeker-200 active:bg-seeker-300 disabled:opacity-100 disabled:bg-seeker-700 disabled:text-seeker-800',
  primaryBlue:
    'bg-blue-400 border-transparent text-black px-4 hover:bg-blue-300 focus-visible:bg-blue-300 active:bg-blue-500 disabled:opacity-100 disabled:bg-seeker-700 disabled:text-seeker-800',
  outlineWhite:
    'bg-transparent border-white text-white px-4 hover:bg-white/10 focus-visible:bg-white/10 active:bg-white/15 disabled:opacity-100 disabled:border-seeker-700 disabled:text-seeker-500',
  outlineMuted:
    'bg-transparent border-seeker-700 text-white px-4 hover:bg-white/5 focus-visible:bg-white/5 active:bg-white/10',
  outlineSuccess:
    'bg-transparent border-green-400 text-green-400 px-4 hover:bg-green-400/10 focus-visible:bg-green-400/10 disabled:opacity-100',
  dangerOutline:
    'bg-transparent border-red-500 text-red-500 px-4 hover:bg-red-500/10 focus-visible:bg-red-500/10 active:bg-red-500/15',
  ghostMuted:
    'bg-transparent border-transparent text-seeker-400 font-normal hover:text-white hover:bg-white/5 focus-visible:text-white active:bg-white/10',
};

export function Button(props: Props) {
  const {
    className,
    children,
    submitting,
    variant = 'default',
    type = 'button',
    ...rest
  } = props;

  return (
    <button
      type={type}
      className={twMerge(
        BASE_CLASSES,
        VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.default,
        className,
      )}
      aria-busy={submitting || undefined}
      {...rest}
    >
      {submitting ? (
        <>
          <LoadingDots />
          <span className="sr-only">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
